import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft, ChevronDown, ChevronRight, Video, FileText, File,
  ImageIcon, Clock, User, Globe, BookOpen, Loader, Download,
  PlayCircle, Tag, Plus, Trash2, X, Upload, Link2, Play, Radio,
  StopCircle,
} from "lucide-react";
import {
  lmsGetCourseById,
  lmsGetClassSubjects,
  lmsGetChapters,
  lmsGetTopics,
  lmsGetMaterials,
  lmsUploadMaterial,
  lmsAddVideoUrl,
  lmsDeleteMaterial,
  lmsCreateLiveClass,
  lmsGetLiveClasses,
} from "../api/lmsApi";
import { ScheduleModal, JoinModal } from "./LiveClasses";
import { useDispatch, useSelector } from "react-redux";
import { fetchLmsCourses, fetchLmsSchoolClasses } from "../redux/lmsSlice";
import "../lms.css";

const getSchoolId = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}").admin_id || null; }
  catch { return null; }
};

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^&?/\s]{11})/);
  return m ? m[1] : null;
}
function getVimeoId(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(\d+)/);
  return m ? m[1] : null;
}
function isImage(url) { return /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url || ""); }
function isPdf(url, docType) { return docType === "pdf" || /\.pdf(\?|$)/i.test(url || ""); }

/* ── Content Viewer ── */
function ContentViewer({ material }) {
  if (!material) {
    return (
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", background: "#0f0f0f", color: "#6b7280" }}>
        <PlayCircle size={56} color="#374151" />
        <p style={{ marginTop: 16, fontSize: 15 }}>Select a lesson to start</p>
      </div>
    );
  }
  const { file_url, material_type, document_type, title, file_name } = material;
  const ytId = getYouTubeId(file_url);
  if (ytId) return (
    <iframe src={`https://www.youtube.com/embed/${ytId}?autoplay=1`} title={title}
      style={{ width: "100%", height: "100%", border: "none" }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen />
  );
  const vimeoId = getVimeoId(file_url);
  if (vimeoId) return (
    <iframe src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1`} title={title}
      style={{ width: "100%", height: "100%", border: "none" }} allowFullScreen />
  );
  if (material_type === "video" || /\.(mp4|webm|ogg)(\?|$)/i.test(file_url || "")) return (
    <video controls autoPlay style={{ width: "100%", height: "100%", background: "#000" }} src={file_url}>
      Your browser does not support video.
    </video>
  );
  if (isImage(file_url)) return (
    <img src={file_url} alt={title} style={{ width: "100%", height: "100%", objectFit: "contain", background: "#111" }} />
  );
  if (isPdf(file_url, document_type)) return (
    <iframe src={file_url} title={title} style={{ width: "100%", height: "100%", border: "none" }} />
  );
  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", background: "#f8fafc", gap: 16 }}>
      <div style={{ width: 80, height: 80, borderRadius: 16, background: "#eff6ff",
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <FileText size={36} color="#1c498d" />
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>{title || file_name || "File"}</div>
      <a href={file_url} target="_blank" rel="noreferrer" style={{
        display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px",
        borderRadius: 8, background: "#1c498d", color: "#fff", fontWeight: 600,
        fontSize: 14, textDecoration: "none" }}>
        <Download size={16} /> Open / Download
      </a>
    </div>
  );
}

function MatTypeIcon({ mat }) {
  const url = mat.file_url || "";
  if (getYouTubeId(url) || getVimeoId(url) || mat.material_type === "video" || /\.(mp4|webm)$/i.test(url))
    return <Video size={13} color="#7c3aed" />;
  if (isImage(url)) return <ImageIcon size={13} color="#0ea5e9" />;
  if (isPdf(url, mat.document_type)) return <FileText size={13} color="#dc2626" />;
  return <File size={13} color="#6b7280" />;
}

/* ── Add Material Modal (matches MasterSetup design) ── */
const DOC_TYPES = [
  { value: "study_material", label: "Study Material" },
  { value: "homework",       label: "Homework" },
  { value: "worksheet",      label: "Worksheet" },
];
const mkFileRow  = () => ({ uid: Date.now() + Math.random(), file: null, title: "", desc: "", docType: "study_material" });
const mkVideoRow = () => ({ uid: Date.now() + Math.random(), title: "", desc: "", url: "", duration: "", docType: "study_material" });

function AddMaterialModal({ topicId, classSubjectId, schoolId, onClose, onSaved }) {
  const [tab, setTab]         = useState("file");
  const [fileRows, setFileRows]   = useState([mkFileRow()]);
  const [videoRows, setVideoRows] = useState([mkVideoRow()]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRefs = React.useRef({});

  const updateFileRow = (uid, field, val) =>
    setFileRows((prev) => prev.map((r) => r.uid === uid ? { ...r, [field]: val } : r));
  const addFileRow    = () => setFileRows((prev) => [...prev, mkFileRow()]);
  const removeFileRow = (uid) => setFileRows((prev) => prev.length > 1 ? prev.filter((r) => r.uid !== uid) : prev);
  const pickFile = (uid, files) => {
    if (files?.[0]) {
      const f = files[0];
      setFileRows((prev) => prev.map((r) => r.uid === uid ? { ...r, file: f, title: r.title || f.name.replace(/\.[^.]+$/, "") } : r));
    }
  };

  const updateVideoRow = (uid, field, val) =>
    setVideoRows((prev) => prev.map((r) => r.uid === uid ? { ...r, [field]: val } : r));
  const addVideoRow    = () => setVideoRows((prev) => [...prev, mkVideoRow()]);
  const removeVideoRow = (uid) => setVideoRows((prev) => prev.length > 1 ? prev.filter((r) => r.uid !== uid) : prev);

  const validFileRows  = fileRows.filter((r) => r.file && r.title.trim());
  const validVideoRows = videoRows.filter((r) => r.title.trim() && r.url.trim());
  const canSubmit = tab === "file" ? validFileRows.length > 0 : validVideoRows.length > 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (tab === "file") {
        await Promise.all(validFileRows.map((r) => {
          const fd = new FormData();
          fd.append("file", r.file);
          fd.append("topic_id", topicId);
          fd.append("class_subject_id", classSubjectId);
          if (schoolId) fd.append("school_id", schoolId);
          fd.append("title", r.title.trim());
          if (r.desc) fd.append("description", r.desc.trim());
          fd.append("document_type", r.docType);
          return lmsUploadMaterial(fd);
        }));
        toast.success(validFileRows.length > 1 ? `${validFileRows.length} files uploaded` : "File uploaded");
      } else {
        await Promise.all(validVideoRows.map((r) =>
          lmsAddVideoUrl({
            topic_id: topicId,
            class_subject_id: classSubjectId,
            school_id: schoolId,
            title: r.title.trim(),
            file_url: r.url.trim(),
            description: r.desc.trim() || null,
            duration_minutes: r.duration ? parseInt(r.duration) : null,
            material_type: "video_link",
            document_type: r.docType,
          })
        ));
        toast.success(validVideoRows.length > 1 ? `${validVideoRows.length} videos added` : "Video link added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add material");
    } finally { setSubmitting(false); }
  };

  const submitLabel = tab === "file"
    ? (validFileRows.length > 1 ? `Upload ${validFileRows.length} Files` : "Upload File")
    : (validVideoRows.length > 1 ? `Add ${validVideoRows.length} Videos` : "Add Video");

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div className="ms2-modal ms2-class-modal ms2-mat-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ms2-modal-header">
          <span>Add Study Material</span>
          <button className="ms2-modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="ms2-mat-tabs">
          <button className={`ms2-mat-tab${tab === "file" ? " ms2-mat-tab--active" : ""}`} onClick={() => setTab("file")}>
            <Upload size={13} /> Content File
          </button>
          <button className={`ms2-mat-tab${tab === "video" ? " ms2-mat-tab--active" : ""}`} onClick={() => setTab("video")}>
            <Play size={13} /> Video Link
          </button>
        </div>

        <div className="ms2-mat-modal-body">
          {tab === "file" && (
            <>
              <div className="ms2-subj-new-header">
                <p className="ms2-class-new-label">Content Files</p>
                <button className="ms2-subj-add-row-btn" onClick={addFileRow} type="button"><Plus size={12} /> Add another</button>
              </div>
              <div className="ms2-subj-rows">
                {fileRows.map((row) => (
                  <div key={row.uid} className="ms2-mat-file-block">
                    <div className="ms2-mat-file-row">
                      {row.file ? (
                        <div className="ms2-mat-file-chip">
                          <FileText size={13} color="#4f46e5" />
                          <span>{row.file.name}</span>
                          <span className="ms2-upload-file-size">{(row.file.size / (1024 * 1024)).toFixed(1)}MB</span>
                          <button className="ms2-subj-remove-btn" onClick={() => updateFileRow(row.uid, "file", null)}><X size={11} /></button>
                        </div>
                      ) : (
                        <button className="ms2-mat-pick-btn" onClick={() => fileInputRefs.current[row.uid]?.click()}>
                          <Upload size={13} /> Choose file
                          <input type="file" style={{ display: "none" }} ref={(el) => (fileInputRefs.current[row.uid] = el)}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp4,.webm,.ogg,.mov,.avi,.mkv,.jpg,.jpeg,.png,.gif,.webp"
                            onChange={(e) => pickFile(row.uid, e.target.files)} />
                        </button>
                      )}
                      <input className="ms2-video-input ms2-mat-title-input" placeholder="Title *"
                        value={row.title} onChange={(e) => updateFileRow(row.uid, "title", e.target.value)} />
                      <select className="ms2-video-input ms2-mat-type-select"
                        value={row.docType} onChange={(e) => updateFileRow(row.uid, "docType", e.target.value)}>
                        {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      {fileRows.length > 1 && (
                        <button className="ms2-subj-remove-btn" onClick={() => removeFileRow(row.uid)}><X size={12} /></button>
                      )}
                    </div>
                    <textarea className="ms2-video-input ms2-mat-desc-textarea" placeholder="Description (optional)" rows={2}
                      value={row.desc} onChange={(e) => updateFileRow(row.uid, "desc", e.target.value)} />
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "video" && (
            <>
              <div className="ms2-subj-new-header">
                <p className="ms2-class-new-label">Video links to add</p>
                <button className="ms2-subj-add-row-btn" onClick={addVideoRow} type="button"><Plus size={12} /> Add another</button>
              </div>
              <div className="ms2-subj-rows">
                {videoRows.map((row, idx) => (
                  <div key={row.uid} className="ms2-mat-file-block">
                    <div className="ms2-mat-video-header-row">
                      <span className="ms2-subj-row-num">{idx + 1}</span>
                      {videoRows.length > 1 && (
                        <button className="ms2-subj-remove-btn ms2-video-remove-btn" onClick={() => removeVideoRow(row.uid)}><X size={12} /></button>
                      )}
                    </div>
                    <div className="ms2-mat-video-2col ms2-video-row1">
                      <select className="ms2-mat-type-select ms2-video-input"
                        value={row.docType} onChange={(e) => updateVideoRow(row.uid, "docType", e.target.value)}>
                        {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <input className="ms2-video-input" placeholder="Title *"
                        value={row.title} onChange={(e) => updateVideoRow(row.uid, "title", e.target.value)} />
                    </div>
                    <div className="ms2-mat-video-2col ms2-video-row2">
                      <input className="ms2-video-input" placeholder="Video URL * (YouTube, Drive…)"
                        value={row.url} onChange={(e) => updateVideoRow(row.uid, "url", e.target.value)} />
                      <input className="ms2-video-input" placeholder="Min" type="number" min="1"
                        value={row.duration} onChange={(e) => updateVideoRow(row.uid, "duration", e.target.value)} />
                    </div>
                    <textarea className="ms2-mat-desc-textarea" placeholder="Description (optional)" rows={2}
                      value={row.desc} onChange={(e) => updateVideoRow(row.uid, "desc", e.target.value)} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="ms2-class-modal-footer">
          <button className="lms-btn-ghost" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="lms-btn-primary" onClick={handleSubmit} disabled={submitting || !canSubmit}>
            {submitting ? <Loader size={13} className="ms2-spin" /> : tab === "file" ? <Upload size={14} /> : <Plus size={14} />}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Mobile sidebar (chapter/topic list below video) ── */
function MobileSidebar({ chapters, matLoading, matError, totalMaterials, activeMaterial, setActiveMaterial, openChapters, toggleChapter }) {
  return (
    <div style={{ borderTop: "2px solid #e5e7eb", background: "#fff" }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", background: "#f8fafc" }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Course Content</div>
        {!matLoading && !matError && (
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
            {chapters.length} chapters · {totalMaterials} lessons
          </div>
        )}
      </div>
      <div>
        {matLoading ? (
          <div style={{ textAlign: "center", padding: 32 }}><Loader size={20} className="ms2-spin" /></div>
        ) : matError ? (
          <div style={{ padding: 20, color: "#6b7280", fontSize: 13, textAlign: "center" }}>{matError}</div>
        ) : chapters.length === 0 ? (
          <div style={{ padding: 20, color: "#9ca3af", fontSize: 13, textAlign: "center" }}>No materials added yet.</div>
        ) : chapters.map((ch, ci) => {
          const chOpen = !!openChapters[ch.id];
          const matCount = ch.topics.reduce((s, t) => s + t.materials.length, 0);
          return (
            <div key={ch.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
              <button onClick={() => toggleChapter(ch.id)} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8,
                padding: "12px 16px", background: chOpen ? "#f0f4ff" : "#f9fafb",
                border: "none", cursor: "pointer", textAlign: "left",
              }}>
                {chOpen ? <ChevronDown size={14} color="#4f46e5" /> : <ChevronRight size={14} color="#9ca3af" />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: "#1e1b4b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    Ch {ch.chapter_number || ci + 1}. {ch.name}
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
                    {ch.topics.length} topics · {matCount} lessons
                  </div>
                </div>
              </button>
              {chOpen && ch.topics.map((tp, ti) => (
                <div key={tp.id}>
                  <div style={{ padding: "6px 16px 4px 36px", fontSize: 11, fontWeight: 700,
                    color: "#7c3aed", background: "#faf5ff", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {tp.topic_number || ti + 1}. {tp.name}
                  </div>
                  {tp.materials.map((m) => {
                    const isActive = activeMaterial?.id === m.id;
                    return (
                      <button key={m.id} onClick={() => setActiveMaterial(m)} style={{
                        width: "100%", display: "flex", alignItems: "center", gap: 10,
                        padding: "9px 16px 9px 44px", border: "none", textAlign: "left",
                        background: isActive ? "#eef2ff" : "transparent",
                        borderLeft: isActive ? "3px solid #4f46e5" : "3px solid transparent",
                        cursor: "pointer",
                      }}>
                        <MatTypeIcon mat={m} />
                        <span style={{ flex: 1, fontSize: 12, color: isActive ? "#4338ca" : "#374151",
                          fontWeight: isActive ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {m.title || m.file_name || "Untitled"}
                        </span>
                        {m.duration_minutes && (
                          <span style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0 }}>{m.duration_minutes}m</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Confirm Delete Modal ── */
function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="ms2-modal-overlay" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, width: 420, maxWidth: "92vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        {/* Top accent bar */}
        <div style={{ height: 4, background: "linear-gradient(90deg,#dc2626,#ef4444)" }} />

        {/* Icon + text body */}
        <div style={{ padding: "32px 28px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
          <div style={{
            width: 60, height: 60, borderRadius: "50%",
            background: "#fef2f2", border: "2px solid #fecaca",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Trash2 size={26} color="#dc2626" />
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 8 }}>Delete Material</div>
            <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7 }}>{message}</div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          display: "flex", gap: 10, padding: "0 28px 24px", justifyContent: "center",
        }}>
          <button onClick={onCancel} style={{
            flex: 1, padding: "10px 0", borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: "1.5px solid #d1d5db", background: "#fff", color: "#374151", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            flex: 1, padding: "10px 0", borderRadius: 8, fontSize: 13, fontWeight: 700,
            border: "none", background: "linear-gradient(135deg,#dc2626,#b91c1c)",
            color: "#fff", cursor: "pointer", display: "flex", alignItems: "center",
            justifyContent: "center", gap: 6,
          }}>
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setWidth(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return width;
}

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const schoolId = getSchoolId();
  const winW = useWindowWidth();
  const isMobile = winW < 768;
  const isTablet = winW >= 768 && winW < 1100;

  const { courses } = useSelector((s) => s.lms);
  const course = courses.find((c) => String(c.id) === String(id));

  const [chapters, setChapters]             = useState([]);
  const [classSubjectId, setClassSubjectId] = useState(null);
  const [matLoading, setMatLoading]         = useState(true);
  const [matError, setMatError]             = useState(null);
  const [openChapters, setOpenChapters]     = useState({});
  const [activeMaterial, setActiveMaterial] = useState(null);
  const [sidebarOpen, setSidebarOpen]       = useState(true);
  const [addMatTopic, setAddMatTopic]       = useState(null); // { id, name }
  const [confirmDelete, setConfirmDelete]   = useState(null); // material object
  const [showLiveModal, setShowLiveModal]           = useState(false);
  const [liveChapterPrefill, setLiveChapterPrefill] = useState(null);
  const [courseLiveClasses, setCourseLiveClasses]   = useState([]);
  const [joinLiveItem, setJoinLiveItem]             = useState(null);
  const schoolClasses = useSelector((s) => s.lms?.schoolClasses || []);

  useEffect(() => {
    if (!courses.length) dispatch(fetchLmsCourses({ school_id: schoolId }));
    if (schoolId) dispatch(fetchLmsSchoolClasses(schoolId));
  }, [dispatch, schoolId, courses.length]);

  // Load chapters → topics → materials cascade
  const loadCourseContent = async (csId) => {
    if (!csId) return [];

    // 1. Fetch chapters for this class_subject
    let rawChapters = [];
    try {
      const chRes = await lmsGetChapters({ class_subject_id: csId });
      rawChapters = Array.isArray(chRes?.data?.data) ? chRes.data.data : [];
    } catch { rawChapters = []; }

    // 2. For each chapter, fetch its topics
    const chaptersWithTopics = await Promise.all(
      rawChapters.filter(Boolean).map(async (ch) => {
        let rawTopics = [];
        try {
          const tpRes = await lmsGetTopics({ chapter_id: ch.id });
          rawTopics = Array.isArray(tpRes?.data?.data) ? tpRes.data.data : [];
        } catch { rawTopics = []; }

        // 3. For each topic, fetch its materials
        const topicsWithMaterials = await Promise.all(
          rawTopics.filter(Boolean).map(async (tp) => {
            let mats = [];
            try {
              const matRes = await lmsGetMaterials({ topic_id: tp.id });
              mats = Array.isArray(matRes?.data?.data) ? matRes.data.data : [];
            } catch { mats = []; }
            return { ...tp, materials: mats };
          })
        );
        return { ...ch, topics: topicsWithMaterials };
      })
    );
    return chaptersWithTopics;
  };

  useEffect(() => {
    setMatLoading(true);
    setMatError(null);

    // Get class_subject_id from course in redux or fetch course by id
    const loadData = async () => {
      try {
        // Step 1: get course fields — from redux or API
        let courseData = course;
        if (!courseData?.class_id) {
          try {
            const courseRes = await lmsGetCourseById(id);
            courseData = courseRes?.data?.data ?? courseRes?.data ?? {};
          } catch { courseData = {}; }
        }

        // Step 2: find class_subject_id by matching class_id + subject_id
        let csId = courseData?.class_subject_id ?? null;
        if (!csId && courseData?.class_id && courseData?.subject_id) {
          try {
            const csRes = await lmsGetClassSubjects({
              class_id: courseData.class_id,
              subject_id: courseData.subject_id,
            });
            const csList = Array.isArray(csRes?.data?.data) ? csRes.data.data : [];
            // Find the matching record
            const match = csList.find(
              (cs) =>
                String(cs.class_id) === String(courseData.class_id) &&
                String(cs.subject_id) === String(courseData.subject_id)
            );
            csId = match?.id ?? (csList[0]?.id ?? null);
          } catch { csId = null; }
        }

        if (!csId) {
          setMatError("No class-subject linked to this course. Please edit the course and assign a Class & Subject.");
          setMatLoading(false);
          return;
        }
        setClassSubjectId(csId);
        const data = await loadCourseContent(csId);
        setChapters(data || []);
        if (data && data.length > 0) {
          setOpenChapters({ [data[0].id]: true });
          const first = data[0]?.topics?.[0]?.materials?.[0] ?? null;
          if (first) setActiveMaterial(first);
        }
      } catch (err) {
        setMatError(err?.response?.data?.message || "Failed to load course content");
      } finally {
        setMatLoading(false);
      }
    };
    loadData();
  }, [id, course?.class_subject_id]);

  // Fetch live classes for this course whenever course class/subject resolves
  useEffect(() => {
    if (!course?.class_id || !schoolId) return;
    lmsGetLiveClasses({ school_id: schoolId, class_id: course.class_id, subject_id: course.subject_id })
      .then((res) => setCourseLiveClasses(res.data.data || []))
      .catch(() => {});
  }, [course?.class_id, course?.subject_id, schoolId]);

  // Compute effective live-class status for a chapter
  const getChapterLiveStatus = (chapterId) => {
    if (!courseLiveClasses.length) return null;
    // Prefer exact chapter match if backend stores chapter_id
    const match = courseLiveClasses.find((lc) => String(lc.chapter_id) === String(chapterId))
      || courseLiveClasses.find((lc) => lc.status === "live")
      || courseLiveClasses.find((lc) => lc.status === "scheduled")
      || courseLiveClasses[0];
    if (!match) return null;

    const now = new Date();
    const start = new Date(match.scheduled_at);
    const end   = new Date(start.getTime() + (match.duration_minutes || 60) * 60000);

    if (match.status === "live" || (now >= start && now <= end)) return { state: "live",      lc: match };
    if (match.status === "ended" || now > end)                   return { state: "ended",     lc: match };
    return                                                               { state: "scheduled", lc: match };
  };

  const toggleChapter = (cid) => setOpenChapters((p) => ({ ...p, [cid]: !p[cid] }));

  const openLiveFromChapter = (ch) => {
    setLiveChapterPrefill({
      class_id:   String(course?.class_id   || ""),
      subject_id: String(course?.subject_id || ""),
      chapter_id: ch.id,
      title: `Ch ${ch.chapter_number || ""}. ${ch.name}`,
    });
    setShowLiveModal(true);
  };

  const reloadMaterials = async () => {
    if (!classSubjectId) return;
    try {
      const data = await loadCourseContent(classSubjectId);
      setChapters(data);
    } catch {}
  };

  const handleDeleteMaterial = (m) => {
    setConfirmDelete(m);
  };

  const doDeleteMaterial = async () => {
    const m = confirmDelete;
    setConfirmDelete(null);
    try {
      await lmsDeleteMaterial(m.id);
      toast.success("Material deleted");
      if (activeMaterial?.id === m.id) setActiveMaterial(null);
      reloadMaterials();
    } catch { toast.error("Failed to delete"); }
  };

  const totalMaterials = chapters.reduce((s, ch) =>
    s + ch.topics.reduce((s2, t) => s2 + t.materials.length, 0), 0);

  const LEVEL_COLORS = {
    beginner:     { bg: "#dcfce7", color: "#166534" },
    intermediate: { bg: "#fef9c3", color: "#854d0e" },
    advanced:     { bg: "#fee2e2", color: "#991b1b" },
  };
  const lvl = LEVEL_COLORS[course?.level] || LEVEL_COLORS.beginner;

  /* Heights:
     LMS topbar = 56px
     Our course topbar = 52px
     Video player height = calc(100vh - 56 - 52) so it exactly fills screen on load
     Below video: description scrolls  */

  const TOPBAR_H = 52;
  const PLAYER_H = isMobile ? "35vh" : isTablet ? "50vh" : "60vh";
  const SIDEBAR_W = isMobile ? "100%" : isTablet ? 300 : 380;
  const SIDEBAR_H = isMobile ? "auto" : `calc(100vh - 56px - ${TOPBAR_H}px)`;

  return (
    /* Escape lms-content padding */
    <div style={{ margin: "-24px" }}>

      {/* ── Course topbar ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 16, padding: "0 20px",
        height: TOPBAR_H, background: "#1e1b4b", borderBottom: "1px solid #312e81",
        position: "sticky", top: -24, zIndex: 100,
      }}>
        <button onClick={() => navigate("/lms/courses")} style={{
          display: "flex", alignItems: "center", gap: 6, background: "transparent",
          border: "none", color: "#a5b4fc", cursor: "pointer", fontWeight: 600,
          fontSize: 13, padding: "4px 8px", borderRadius: 6,
        }}>
          <ArrowLeft size={16} /> Back
        </button>
        <div style={{ width: 1, height: 20, background: "#312e81" }} />
        <div style={{ fontWeight: 700, fontSize: 14, color: "#e0e7ff", flex: 1,
          overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
          {course?.name || "Course"}
        </div>
        {course?.level && !isMobile && (
          <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px",
            borderRadius: 20, background: lvl.bg, color: lvl.color }}>
            {course.level}
          </span>
        )}
        <button onClick={() => setShowLiveModal(true)} title="Schedule a live class for this course" style={{
          display: "flex", alignItems: "center", gap: 6, padding: "5px 12px",
          borderRadius: 6, border: "1px solid #16a34a", background: "#166534",
          color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600,
        }}>
          <Radio size={14} /> Live Class
        </button>
        {!isMobile && (
          <button onClick={() => setSidebarOpen((p) => !p)} title={sidebarOpen ? "Hide content" : "Show content"} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "5px 12px",
            borderRadius: 6, border: "1px solid #312e81", background: sidebarOpen ? "#312e81" : "transparent",
            color: "#a5b4fc", cursor: "pointer", fontSize: 12, fontWeight: 600,
          }}>
            <BookOpen size={14} />
            {sidebarOpen ? "Hide" : "Content"}
          </button>
        )}
      </div>

      {/* ── Main layout: left scrolls, right sticky ── */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: "flex-start", background: "#fff" }}>

        {/* LEFT — scrolls naturally */}
        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Video player */}
          <div style={{ height: PLAYER_H, background: "#0f0f0f" }}>
            <ContentViewer material={activeMaterial} />
          </div>

          {/* MOBILE: Course Content right after video */}
          {isMobile && <MobileSidebar
            chapters={chapters} matLoading={matLoading} matError={matError}
            totalMaterials={totalMaterials} activeMaterial={activeMaterial}
            setActiveMaterial={setActiveMaterial} openChapters={openChapters}
            toggleChapter={toggleChapter}
          />}

          {/* Tabs row */}
          <div style={{ borderBottom: "2px solid #e5e7eb", padding: "0 24px",
            display: "flex", gap: 0, background: "#fff", position: "sticky",
            top: TOPBAR_H - 24, zIndex: 10 }}>
            <div style={{ padding: "12px 0", marginRight: 24, fontSize: 14, fontWeight: 700,
              color: "#4f46e5", borderBottom: "2px solid #4f46e5", marginBottom: -2 }}>
              Overview
            </div>
          </div>

          {/* Description & details — scrolls */}
          <div style={{ padding: "28px 24px", background: "#fff" }}>
            {course ? (
              <>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: "0 0 12px" }}>
                  {course.name}
                </h1>
                {course.description && (
                  <p style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.8, marginBottom: 24 }}>
                    {course.description}
                  </p>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 20, padding: "20px 0",
                  borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb", marginBottom: 20 }}>
                  {course.instructor_name && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151" }}>
                      <User size={15} color="#9ca3af" /> <strong>Instructor:</strong>&nbsp;{course.instructor_name}
                    </div>
                  )}
                  {course.language && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151" }}>
                      <Globe size={15} color="#9ca3af" /> {course.language}
                    </div>
                  )}
                  {course.duration_days && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151" }}>
                      <Clock size={15} color="#9ca3af" /> {course.duration_days} days
                    </div>
                  )}
                  {course.class_name && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151" }}>
                      <BookOpen size={15} color="#9ca3af" /> {course.class_name} — {course.subject_name}
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                    <Tag size={15} color="#9ca3af" />
                    {course.is_free
                      ? <span style={{ color: "#166534", fontWeight: 700 }}>Free</span>
                      : <span style={{ color: "#1c498d", fontWeight: 700 }}>
                          ₹{parseFloat(course.discount_price || course.price || 0).toFixed(2)}
                        </span>}
                  </div>
                </div>
                {activeMaterial && (
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 4 }}>
                      Now Playing: {activeMaterial.title || activeMaterial.file_name}
                    </div>
                    {activeMaterial.duration_minutes && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#6b7280" }}>
                        <Clock size={12} /> {activeMaterial.duration_minutes} min
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Loader size={24} className="ms2-spin" />
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — sticky sidebar (desktop/tablet only, toggleable) */}
        {!isMobile && sidebarOpen && <div style={{
          width: SIDEBAR_W, flexShrink: 0, borderLeft: "1px solid #e5e7eb",
          position: "sticky", top: TOPBAR_H - 24,
          height: SIDEBAR_H, display: "flex", flexDirection: "column",
          background: "#fff", overflow: "hidden",
        }}>
          {/* Sidebar header */}
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb",
            background: "#f8fafc", flexShrink: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Course Content</div>
            {!matLoading && !matError && (
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                {chapters.length} chapters · {totalMaterials} lessons
              </div>
            )}
          </div>

          {/* Scrollable chapter/topic/material list */}
          <div style={{ flex: 1, overflowY: "auto", maxHeight: isMobile ? 360 : undefined }}>
            {matLoading ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Loader size={22} className="ms2-spin" />
              </div>
            ) : matError ? (
              <div style={{ padding: 24, color: "#6b7280", fontSize: 13, textAlign: "center" }}>{matError}</div>
            ) : chapters.length === 0 ? (
              <div style={{ padding: 24, color: "#9ca3af", fontSize: 13, textAlign: "center" }}>No materials added yet.</div>
            ) : chapters.map((ch, ci) => {
              const chOpen = !!openChapters[ch.id];
              const matCount = ch.topics.reduce((s, t) => s + t.materials.length, 0);
              return (
                <div key={ch.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <div style={{ display: "flex", alignItems: "center", background: chOpen ? "#f0f4ff" : "#f9fafb" }}>
                    <button onClick={() => toggleChapter(ch.id)} style={{
                      flex: 1, display: "flex", alignItems: "center", gap: 8,
                      padding: "12px 16px", background: "transparent",
                      border: "none", cursor: "pointer", textAlign: "left",
                    }}>
                      {chOpen ? <ChevronDown size={14} color="#4f46e5" /> : <ChevronRight size={14} color="#9ca3af" />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: "#1e1b4b",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          Ch {ch.chapter_number || ci + 1}. {ch.name}
                        </div>
                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
                          {ch.topics.length} topics · {matCount} lessons
                        </div>
                      </div>
                    </button>
                    {(() => {
                      const ls = getChapterLiveStatus(ch.id);
                      if (ls?.state === "live") return (
                        <button
                          onClick={() => setJoinLiveItem(ls.lc)}
                          title="Join live class"
                          style={{
                            flexShrink: 0, display: "flex", alignItems: "center", gap: 4,
                            padding: "3px 8px", borderRadius: 6, border: "none",
                            background: "#16a34a", color: "#fff", cursor: "pointer",
                            fontSize: 11, fontWeight: 700, marginRight: 10,
                            animation: "pulse 1.5s infinite",
                          }}
                        >
                          <Radio size={11} /> Join
                        </button>
                      );
                      if (ls?.state === "scheduled") return (
                        <button disabled title="Upcoming — not started yet" style={{
                          flexShrink: 0, display: "flex", alignItems: "center", gap: 4,
                          padding: "3px 8px", borderRadius: 6, border: "none",
                          background: "#f59e0b", color: "#fff", cursor: "not-allowed",
                          fontSize: 11, fontWeight: 700, marginRight: 10, opacity: 0.75,
                        }}>
                          <Clock size={11} /> Soon
                        </button>
                      );
                      if (ls?.state === "ended") return (
                        <button disabled title="Class ended" style={{
                          flexShrink: 0, display: "flex", alignItems: "center", gap: 4,
                          padding: "3px 8px", borderRadius: 6, border: "none",
                          background: "#9ca3af", color: "#fff", cursor: "not-allowed",
                          fontSize: 11, fontWeight: 700, marginRight: 10,
                        }}>
                          <StopCircle size={11} /> Ended
                        </button>
                      );
                      return (
                        <button
                          onClick={() => openLiveFromChapter(ch)}
                          title="Schedule live class for this chapter"
                          style={{
                            flexShrink: 0, width: 24, height: 24, borderRadius: 6,
                            border: "none", background: "#16a34a", color: "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer", marginRight: 10,
                          }}
                        >
                          <Radio size={12} />
                        </button>
                      );
                    })()}
                  </div>

                  {chOpen && ch.topics.map((tp, ti) => (
                    <div key={tp.id}>
                      {/* Topic header with Add button */}
                      <div style={{ padding: "7px 12px 5px 36px", fontSize: 11, fontWeight: 700,
                        color: "#7c3aed", background: "#faf5ff",
                        textTransform: "uppercase", letterSpacing: "0.05em",
                        display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {tp.topic_number || ti + 1}. {tp.name}
                        </span>
                        <button onClick={() => setAddMatTopic(tp)} title="Add material" style={{
                          flexShrink: 0, width: 20, height: 20, borderRadius: 4,
                          border: "none", background: "#7c3aed", color: "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer", marginLeft: 6,
                        }}><Plus size={11} /></button>
                      </div>
                      {tp.materials.map((m) => {
                        const isActive = activeMaterial?.id === m.id;
                        return (
                          <div key={m.id} style={{
                            display: "flex", alignItems: "center",
                            borderLeft: isActive ? "3px solid #4f46e5" : "3px solid transparent",
                            background: isActive ? "#eef2ff" : "transparent",
                          }}>
                            <button onClick={() => setActiveMaterial(m)} style={{
                              flex: 1, display: "flex", alignItems: "center", gap: 8,
                              padding: "8px 8px 8px 44px", border: "none", textAlign: "left",
                              background: "transparent", cursor: "pointer",
                            }}>
                              <MatTypeIcon mat={m} />
                              <span style={{ flex: 1, fontSize: 12,
                                color: isActive ? "#4338ca" : "#374151",
                                fontWeight: isActive ? 600 : 400,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {m.title || m.file_name || "Untitled"}
                              </span>
                              {m.duration_minutes && (
                                <span style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0 }}>{m.duration_minutes}m</span>
                              )}
                            </button>
                            <button onClick={() => handleDeleteMaterial(m)} title="Delete" style={{
                              flexShrink: 0, padding: "4px 8px", border: "none",
                              background: "transparent", color: "#dc2626", cursor: "pointer",
                            }}><Trash2 size={12} /></button>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>}
      </div>

      {/* Confirm Delete Modal */}
      {confirmDelete && (
        <ConfirmModal
          title="Delete Material"
          message={<>Are you sure you want to delete <strong style={{ color: "#111827" }}>"{confirmDelete.title || confirmDelete.file_name}"</strong>?<br />This action cannot be undone.</>}
          onConfirm={doDeleteMaterial}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Add Material Modal */}
      {addMatTopic && classSubjectId && (
        <AddMaterialModal
          topicId={addMatTopic.id}
          classSubjectId={classSubjectId}
          schoolId={schoolId}
          onClose={() => setAddMatTopic(null)}
          onSaved={reloadMaterials}
        />
      )}

      {/* Join Live Class Modal */}
      {joinLiveItem && (
        <JoinModal liveClass={joinLiveItem} onClose={() => setJoinLiveItem(null)} />
      )}

      {/* Schedule Live Class Modal — class, subject & chapter pre-filled */}
      {showLiveModal && (
        <ScheduleModal
          onClose={() => { setShowLiveModal(false); setLiveChapterPrefill(null); }}
          onSave={async (form) => {
            await lmsCreateLiveClass({ ...form, school_id: schoolId });
            toast.success("Live class scheduled!");
          }}
          schoolClasses={schoolClasses}
          schoolId={schoolId}
          chapters={chapters}
          prefill={liveChapterPrefill || {
            class_id:   String(course?.class_id   || ""),
            subject_id: String(course?.subject_id || ""),
          }}
        />
      )}
    </div>
  );
}
